/**
 * Login Page (Web)
 * Google authentication login page using @react-oauth/google
 * In development mode (REACT_APP_DEV_AUTH_ENABLED=true), shows a dev user selector
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import {
  MDBContainer,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBTypography,
  MDBInput,
  MDBBtn,
  MDBListGroup,
  MDBListGroupItem,
  MDBBadge,
} from 'mdb-react-ui-kit';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import './Login.css';

const Login: React.FC = () => {
  const { isLoading, refreshAuthState } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  
  const isDevMode = authService.isDevAuthEnabled();
  const devUsers = authService.getDevUsers();

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      setError(null);
      setIsSigningIn(true);
      
      if (!credentialResponse.credential) {
        throw new Error('No credential received from Google');
      }
      
      // Send the ID token to our Easy Auth emulator / Azure Easy Auth
      const success = await authService.signInWithIdToken(credentialResponse.credential);
      
      if (success) {
        // Refresh the auth context
        await refreshAuthState();
        // Navigate to home or the original destination
        const redirectUrl = sessionStorage.getItem('auth_redirect_url') || '/';
        sessionStorage.removeItem('auth_redirect_url');
        navigate(redirectUrl, { replace: true });
      } else {
        throw new Error('Backend authentication failed');
      }
    } catch (err) {
      setError('Failed to sign in. Please try again.');
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in failed. Please try again.');
  };

  const handleDevLogin = async (email: string) => {
    try {
      setError(null);
      setIsSigningIn(true);
      
      const result = await authService.devLogin(email);
      
      if (result.success) {
        // Refresh the auth context
        await refreshAuthState();
        // Navigate to home or pending approval page (AuthContext handles routing)
        const redirectUrl = sessionStorage.getItem('auth_redirect_url') || '/';
        sessionStorage.removeItem('auth_redirect_url');
        navigate(redirectUrl, { replace: true });
      } else {
        setError(result.error || 'Dev login failed');
      }
    } catch (err) {
      setError('Failed to sign in. Please try again.');
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleCustomEmailLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (customEmail.trim()) {
      handleDevLogin(customEmail.trim());
    }
  };

  if (isLoading || isSigningIn) {
    return (
      <MDBContainer className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <MDBCard style={{ maxWidth: '500px', width: '100%' }}>
          <MDBCardBody className="text-center py-5">
            <MDBTypography tag="h1" className="mb-3">SolidRange</MDBTypography>
            <MDBIcon fas icon="spinner" spin size="2x" className="text-primary" />
            <MDBTypography className="mt-3">
              {isSigningIn ? 'Signing in...' : 'Loading...'}
            </MDBTypography>
          </MDBCardBody>
        </MDBCard>
      </MDBContainer>
    );
  }

  // Development mode login UI
  if (isDevMode) {
    return (
      <MDBContainer className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <MDBCard style={{ maxWidth: '500px', width: '100%' }}>
          <MDBCardBody className="p-4">
            <div className="text-center mb-4">
              <img src="/SolidZero.png" alt="SolidZero" height="80" className="mb-3 logo-transparent" />
              <MDBTypography tag="h4" className="mb-1">SolidRange</MDBTypography>
              <MDBBadge color="warning" className="mb-3">Development Mode</MDBBadge>
              <MDBTypography className="text-muted small">
                Select a dev user or enter a custom email to login
              </MDBTypography>
            </div>
            
            {error && (
              <div className="alert alert-danger mb-3" role="alert">
                <MDBIcon fas icon="exclamation-circle" className="me-2" />
                {error}
              </div>
            )}

            <MDBTypography tag="h6" className="mb-2">
              <MDBIcon fas icon="users" className="me-2" />
              Pre-defined Users
            </MDBTypography>
            
            <MDBListGroup className="mb-4">
              {devUsers.map((user) => (
                <MDBListGroupItem 
                  key={user.email}
                  action
                  className="d-flex justify-content-between align-items-center"
                  onClick={() => handleDevLogin(user.email)}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <strong>{user.displayName}</strong>
                    <br />
                    <small className="text-muted">{user.email}</small>
                  </div>
                  <MDBBadge 
                    color={user.email === 'dev@solidrange.local' ? 'success' : 'warning'}
                    pill
                  >
                    {user.email === 'dev@solidrange.local' ? 'Approved' : 'Pending'}
                  </MDBBadge>
                </MDBListGroupItem>
              ))}
            </MDBListGroup>

            <MDBTypography tag="h6" className="mb-2">
              <MDBIcon fas icon="user-plus" className="me-2" />
              Custom User
            </MDBTypography>
            
            <form onSubmit={handleCustomEmailLogin}>
              <div className="d-flex gap-2">
                <MDBInput
                  type="email"
                  label="Custom email address"
                  value={customEmail}
                  onChange={(e) => setCustomEmail(e.target.value)}
                  className="flex-grow-1"
                />
                <MDBBtn type="submit" color="primary" disabled={!customEmail.trim()}>
                  Login
                </MDBBtn>
              </div>
              <small className="text-muted d-block mt-2">
                <MDBIcon fas icon="info-circle" className="me-1" />
                Custom users start with pending approval status
              </small>
            </form>

            <hr className="my-4" />
            
            <div className="text-center">
              <small className="text-muted">
                <MDBIcon fas icon="flask" className="me-1" />
                Dev mode is enabled via <code>REACT_APP_DEV_AUTH_ENABLED=true</code>
              </small>
            </div>
          </MDBCardBody>
        </MDBCard>
      </MDBContainer>
    );
  }

  // Production mode - Google OAuth login
  return (
    <MDBContainer className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
      <MDBCard style={{ maxWidth: '500px', width: '100%' }}>
        <MDBCardBody className="text-center p-5">
          <img src="/SolidZero.png" alt="SolidZero" height="100" className="mb-4 logo-transparent" />
          <MDBTypography tag="h1" className="mb-2">SolidRange</MDBTypography>
          <MDBTypography className="text-muted mb-4">Track your shooting sessions</MDBTypography>
          
          <div className="my-4">
            <MDBTypography className="mb-4">
              Sign in to access your shooting data across all devices.
            </MDBTypography>
            
            {error && (
              <div className="alert alert-danger mb-4" role="alert">
                <MDBIcon fas icon="exclamation-circle" className="me-2" />
                {error}
              </div>
            )}
            
            <div className="d-flex justify-content-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                size="large"
                theme="outline"
                text="signin_with"
                shape="rectangular"
                width="300"
              />
            </div>

            <MDBTypography className="text-muted small mt-4">
              <MDBIcon fas icon="lock" className="me-2" />
              Your shooting session data will be securely synced across all your devices.
            </MDBTypography>
            
            <MDBTypography className="text-muted small mt-3 pt-3" style={{ borderTop: '1px solid rgba(0,0,0,0.1)', fontSize: '0.75rem', lineHeight: '1.3' }}>
              By logging in, you consent to the use of this app and agree that your data may be used for analysis purposes to improve the service.
            </MDBTypography>
            
            <MDBTypography className="text-muted small mt-3" style={{ fontSize: '0.75rem' }}>
              <Link to="/privacy-policy" className="text-decoration-none me-3">Privacy Policy</Link>
              <Link to="/terms-of-service" className="text-decoration-none">Terms of Service</Link>
            </MDBTypography>
          </div>
        </MDBCardBody>
      </MDBCard>
    </MDBContainer>
  );
};

export default Login;
